import { LinkIcon } from '@chakra-ui/icons';
import { Button, Flex, Stack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { DataTable } from '../../components/Table/DataTable';
import useWhiteLabelOrganizationsQuery from '../../hooks/useWhiteLabelOrganizationsQuery';
import { ContentCard } from '../../layout/ContentCard';

const WhiteLabelOverviewCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialFilters = {
    searchString: '',
    itemsPerPage: 20,
  };

  const localStorageData = window.localStorage.getItem('whitelabel_organizations_filters');
  // @ts-ignore
  const destringifiedData = JSON.parse(localStorageData) || {};
  useEffect(() => {
    if (!localStorageData) {
      window.localStorage.setItem('whitelabel_organizations_filters', JSON.stringify(initialFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchString, setSearchString] = useState(destringifiedData?.searchString || initialFilters?.searchString);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(destringifiedData?.itemsPerPage || initialFilters?.itemsPerPage);
  const whiteLabelOrganizationsQuery = useWhiteLabelOrganizationsQuery({
    searchString,
    itemsPerPage,
    page,
  });
  // @ts-ignore
  const whiteLabelOrganizations = whiteLabelOrganizationsQuery?.data?.data || [];
  // @ts-ignore
  const currentPage = whiteLabelOrganizationsQuery.data?.meta?.current_page - 1;
  const initialLoad = searchString === '' && itemsPerPage === 20 && page === 1;

  useEffect(() => {
    setPage(1);
  }, [searchString, itemsPerPage]);
  // @ts-ignore

  // eslint-disable-next-line no-undef
  const columns = useMemo(
    () => [
      {
        Header: t('common:id'),
        accessor: 'id',
      },
      {
        Header: t('common:name'),
        accessor: 'name',
      },
      {
        Header: t('common:domain'),
        accessor: 'domain',
      },
      {
        id: 'button',
        accessor: 'id',
        Cell: (cellProps: { row: any }) => {
          const { row } = cellProps;
          return (
            <Flex alignContent={'flex-end'}>
              <Button
                ml={'auto'}
                onClick={() => {
                  navigate(`/app/op/whitelabel-organizations/${row.original.id}`);
                }}
              >
                {t('common:details')}
              </Button>
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [whiteLabelOrganizations, searchString, page, itemsPerPage],
  );

  if (whiteLabelOrganizationsQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ContentCard
      icon={<LinkIcon boxSize={10} />}
      minH="fit-content"
      header={t('pages:whitelabel_organizations.overview_card.title')}
      contentDescription={t('pages:whitelabel_organizations.overview_card.description')}
      data-test-id="whitelabel-organizations-overview-card"
    >
      <Button variant={'primary'} onClick={() => navigate('/app/op/whitelabel-organizations/create')}>
        {t('common:create')}
      </Button>
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            resetFilter={() => {
              setSearchString(initialFilters?.searchString);
              setPage(1);
              setItemsPerPage(initialFilters?.itemsPerPage);
            }}
            globalFilter={searchString}
            autoFocus={true}
            // @ts-ignore
            setGlobalFilter={setSearchString}
            globalFilterPlaceholder={t('chains_filters:search')}
            //@ts-ignore
            hasFilters={false}
            hasSearchInput={true}
            currentPage={currentPage}
            perPage={itemsPerPage}
            data-test-id="whitelabel-organizations-table"
            data={whiteLabelOrganizations}
            // @ts-ignore
            total={whiteLabelOrganizationsQuery.data?.meta?.last_page || 1}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            isMoreThenOnePage={
              !initialLoad ||
              // @ts-ignore
              Boolean(whiteLabelOrganizationsQuery.data?.links?.last || currentPage > 0)
            }
            columns={columns}
            loading={whiteLabelOrganizationsQuery.isLoading}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WhiteLabelOverviewCard;
